import React, { useState, useEffect, Suspense, lazy } from "react";
import { tr } from "@/i18n/tr";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import axios, { API } from "@/lib/api";
import { LangProvider } from "@/context/LangContext";
import { CountryProvider } from "@/context/CountryContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { IndustryProvider } from "@/context/IndustryContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import AdminLayout from "@/components/AdminLayout";
import { installOfflineInterceptor, installOfflineReadCache, replayQueue, getQueueCount } from "@/utils/offlineSync";
import { drainToastBuffer } from "@/utils/toastBus";
import { applyStoreTheme, applyCachedTheme } from "@/utils/theme";
import { setCurSym } from "@/utils/cur";
import { rememberRawDriver } from "@/utils/rawDriverFlag";
import ApkUpdateChecker from "@/components/ApkUpdateChecker";
import { DesktopAutoUpdate } from "@/components/DesktopAutoUpdate";
import { RemoteAutoJoin } from "@/components/RemoteAutoJoin";
import { CacheWarmer } from "@/components/CacheWarmer";
import { getShopSubdomain } from "@/utils/shopUrl";
const SHOP_SUB = getShopSubdomain();

// Eager load: login/landing/register (needed immediately)
import LoginPage from "@/pages/LoginPage";
import LandingPage from "@/pages/LandingPage";
import SaasCheckoutPage, { SaasSuccessPage } from "@/pages/SaasCheckoutPage";

// Lazy load everything else — separate chunks, loaded on demand
const LandingInternational = lazy(() => import("@/pages/LandingInternational"));
const LandingZA = lazy(() => import("@/pages/LandingZA"));
const LandingCO = lazy(() => import("@/pages/LandingCO"));
const LandingBR = lazy(() => import("@/pages/LandingBR"));
const LandingCR = lazy(() => import("@/pages/LandingCR"));
const LandingTT = lazy(() => import("@/pages/LandingTT"));
const LandingDO = lazy(() => import("@/pages/LandingDO"));
const LandingPA = lazy(() => import("@/pages/LandingPA"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const SalesDocsPage = lazy(() => import("@/pages/admin/SalesDocsPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const TerminosPage = lazy(() => import("@/pages/TerminosPage"));
const PosPage = lazy(() => import("@/pages/PosPage"));
const KdsPage = lazy(() => import("@/pages/KdsPage"));
const PickupScreenPage = lazy(() => import("@/pages/PickupScreenPage"));
const AttendanceKioskPage = lazy(() => import("@/pages/AttendanceKioskPage"));
const PhoneClockPage = lazy(() => import("@/pages/PhoneClockPage"));
const MobilePosPage = lazy(() => import("@/pages/MobilePosPage"));
const PrintSetupPage = lazy(() => import("@/pages/PrintSetupPage"));
const ShopPage = lazy(() => import("@/pages/ShopPage"));
const ShopOrdersPage = lazy(() => import("@/pages/ShopOrdersPage"));
const ShopHowToBuyPage = lazy(() => import("@/pages/ShopHowToBuyPage"));
const CatalogPage = lazy(() => import("@/pages/CatalogPage"));
const ReceiptPublicPage = lazy(() => import("@/pages/ReceiptPublicPage"));
const EmployeeStatementPublicPage = lazy(() => import("@/pages/EmployeeStatementPublicPage"));
const CustomerStatementPublicPage = lazy(() => import("@/pages/CustomerStatementPublicPage"));
const AdminSecretManual = lazy(() => import("@/pages/AdminSecretManual"));
const MemberLookupPage = lazy(() => import("@/pages/MemberLookupPage"));
const VerifyPaymentPage = lazy(() => import("@/pages/VerifyPaymentPage"));
const RiderApp = lazy(() => import("@/pages/RiderApp"));
const RiderTrackPage = lazy(() => import("@/pages/RiderTrackPage"));
const TruckDriverPage = lazy(() => import("@/pages/TruckDriverPage"));
const RateOrderPage = lazy(() => import("@/pages/RateOrderPage"));

// Admin Pages - all lazy loaded
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const StoresPage = lazy(() => import("@/pages/admin/StoresPage"));
const StorePricesPage = lazy(() => import("@/pages/admin/StorePricesPage"));
const WarehousesPage = lazy(() => import("@/pages/admin/WarehousesPage"));
const ProductsPage = lazy(() => import("@/pages/admin/ProductsPage"));
const TransferPage = lazy(() => import("@/pages/admin/TransferPage"));
const CustomersPage = lazy(() => import("@/pages/admin/CustomersPage"));
const SuppliersPage = lazy(() => import("@/pages/admin/SuppliersPage"));
const PayablesPage = lazy(() => import("@/pages/admin/PayablesPage"));
const VouchersPage = lazy(() => import("@/pages/admin/VouchersPage"));
const PriceChangesPage = lazy(() => import("@/pages/admin/PriceChangesPage"));
const CustomsPage = lazy(() => import("@/pages/admin/CustomsPage"));
const FabricaPage = lazy(() => import("@/pages/admin/FabricaPage"));
const FranchisePage = lazy(() => import("@/pages/admin/FranchisePage"));
const FranchisePortalPage = lazy(() => import("@/pages/FranchisePortalPage"));
const PurchasesPage = lazy(() => import("@/pages/admin/PurchasesPage"));
const SalesPage = lazy(() => import("@/pages/admin/SalesPage"));
const OnlineOrdersPage = lazy(() => import("@/pages/admin/OnlineOrdersPage"));
const SalesReportPage = lazy(() => import("@/pages/admin/SalesReportPage"));
const ReportsHubPage = lazy(() => import("@/pages/admin/ReportsHubPage"));
const ContabilidadPage = lazy(() => import("@/pages/admin/ContabilidadPage"));
const CajaEbrPage = lazy(() => import("@/pages/admin/CajaEbrPage"));
const VideoTutorialsPage = lazy(() => import("@/pages/admin/VideoTutorialsPage"));
const ExchangeRatesPage = lazy(() => import("@/pages/admin/ExchangeRatesPage"));
const BankAccountsPage = lazy(() => import("@/pages/admin/BankAccountsPage"));
const CasheaReconcilePage = lazy(() => import("@/pages/admin/CasheaReconcilePage"));
const PaymentSettingsPage = lazy(() => import("@/pages/admin/PaymentSettingsPage"));
const SystemSettingsPage = lazy(() => import("@/pages/admin/SystemSettingsPage"));
const DocDesignPage = lazy(() => import("@/pages/admin/DocDesignPage"));
const TutorialsPage = lazy(() => import("@/pages/admin/TutorialsPage"));
const PrinterGuidePage = lazy(() => import("@/pages/admin/PrinterGuidePage"));
const EmployeesPage = lazy(() => import("@/pages/admin/EmployeesPage"));
const StockAlertsPage = lazy(() => import("@/pages/admin/StockAlertsPage"));
const StockTakingPage = lazy(() => import("@/pages/admin/StockTakingPage"));
const CategoriesPage = lazy(() => import("@/pages/admin/CategoriesPage"));
const DailySettlementPage = lazy(() => import("@/pages/admin/DailySettlementPage"));
const PointsMonthlyPage = lazy(() => import("@/pages/admin/PointsMonthlyPage"));
const RefundsPage = lazy(() => import("@/pages/admin/RefundsPage"));
const ProfitAnalysisPage = lazy(() => import("@/pages/admin/ProfitAnalysisPage"));
const TopSellersPage = lazy(() => import("@/pages/admin/TopSellersPage"));
const SlowMoversPage = lazy(() => import("@/pages/admin/SlowMoversPage"));
const AuditLogPage = lazy(() => import("@/pages/admin/AuditLogPage"));
const TrustedDevicesPage = lazy(() => import("@/pages/admin/TrustedDevicesPage"));
const DeviceApprovalsPage = lazy(() => import("@/pages/admin/DeviceApprovalsPage"));
const BossPinPage = lazy(() => import("@/pages/admin/BossPinPage"));
const PromotionsPage = lazy(() => import("@/pages/admin/PromotionsPage"));
const AccountsPage = lazy(() => import("@/pages/admin/AccountsPage"));
const WholesalePage = lazy(() => import("@/pages/admin/WholesalePage"));
const AttendancePage = lazy(() => import("@/pages/admin/AttendancePage"));
const SalesTargetsPage = lazy(() => import("@/pages/admin/SalesTargetsPage"));
const PurchaseReturnsPage = lazy(() => import("@/pages/admin/PurchaseReturnsPage"));
const BundlesPage = lazy(() => import("@/pages/admin/BundlesPage"));
const NotificationsPage = lazy(() => import("@/pages/admin/NotificationsPage"));
const CommissionPage = lazy(() => import("@/pages/admin/CommissionPage"));
const ShiftReportsPage = lazy(() => import("@/pages/admin/ShiftReportsPage"));
const ShiftReconcilePage = lazy(() => import("@/pages/admin/ShiftReconcilePage"));
const EmailReportsPage = lazy(() => import("@/pages/admin/EmailReportsPage"));
const LoyaltyProgramPage = lazy(() => import("@/pages/admin/LoyaltyProgramPage"));
const PointsMallPage = lazy(() => import("@/pages/admin/PointsMallPage"));
const CreditPage = lazy(() => import("@/pages/admin/CreditPage"));
const FiscalPage = lazy(() => import("@/pages/admin/FiscalPage"));
const EInvoicePage = lazy(() => import("@/pages/admin/EInvoicePage"));
const MarketingPage = lazy(() => import("@/pages/admin/MarketingPage"));
const MarketingAutoPage = lazy(() => import("@/pages/admin/MarketingAutoPage"));
const MarketingBlastPage = lazy(() => import("@/pages/admin/MarketingBlastPage"));
const AiCreditsTopupPage = lazy(() => import("@/pages/admin/AiCreditsTopupPage"));
const ClothingVariantStockPage = lazy(() => import("@/pages/admin/ClothingVariantStockPage"));
const ApartadosPage = lazy(() => import("@/pages/admin/ApartadosPage"));
const PartnerLedgerPage = lazy(() => import("@/pages/admin/PartnerLedgerPage"));
const ApartaPublicPage = lazy(() => import("@/pages/ApartaPublicPage"));
const ProductImageUploadPublicPage = lazy(() => import("@/pages/ProductImageUploadPublicPage"));
const BatchImageUploadPublicPage = lazy(() => import("@/pages/BatchImageUploadPublicPage"));
const BossCameraPage = lazy(() => import("@/pages/BossCameraPage"));
const FxAlertPage = lazy(() => import("@/pages/admin/FxAlertPage"));
const IntlPaymentPage = lazy(() => import("@/pages/admin/IntlPaymentPage"));
const PublicPointsPage = lazy(() => import("@/pages/PublicPointsPage"));
const CodeStudioPage = lazy(() => import("@/pages/DevStudioPage"));
const PointsQRPosterPage = lazy(() => import("@/pages/admin/PointsQRPosterPage"));
const SuperCountryPage = lazy(() => import("@/pages/admin/SuperCountryPage"));
const SuperTrafficPage = lazy(() => import("@/pages/admin/SuperTrafficPage"));
const SuperCryptoIncomePage = lazy(() => import("@/pages/admin/SuperCryptoIncomePage"));
const SuperReferralCommissionsPage = lazy(() => import("@/pages/admin/SuperReferralCommissionsPage"));
const SuperDirectPayPage = lazy(() => import("@/pages/admin/SuperDirectPayPage"));
const SuperManualTopupsPage = lazy(() => import("@/pages/admin/SuperManualTopupsPage"));
const SuperAIUsagePage = lazy(() => import("@/pages/admin/SuperAIUsagePage"));
const GroupBuyPage = lazy(() => import("@/pages/admin/GroupBuyPage"));
const VendedorApp = lazy(() => import("@/pages/VendedorApp"));
const CountTaskPage = lazy(() => import("@/pages/CountTaskPage"));
const TvPage = lazy(() => import("@/pages/TvPage"));
const SorteoTvPage = lazy(() => import("@/pages/SorteoTvPage"));
const TvCamerasPage = lazy(() => import("@/pages/TvCamerasPage"));
const SorteoRegisterPage = lazy(() => import("@/pages/SorteoRegisterPage"));
const SorteoPage = lazy(() => import("@/pages/admin/SorteoPage"));
const HrLegalPage = lazy(() => import("@/pages/admin/HrLegalPage"));
const ShopMarketingPage = lazy(() => import("@/pages/admin/ShopMarketingPage"));
const PickingPage = lazy(() => import("@/pages/admin/PickingPage"));
const SuperSubscriptionsPage = lazy(() => import("@/pages/admin/SuperSubscriptionsPage"));
const SuperTicketsPage = lazy(() => import("@/pages/admin/SuperTicketsPage"));
const SuperReleaseNotesPage = lazy(() => import("@/pages/admin/SuperReleaseNotesPage"));
const SupportTicketsPage = lazy(() => import("@/pages/admin/SupportTicketsPage"));
const B2BPortalPage = lazy(() => import("@/pages/admin/B2BPortalPage"));
const SmartRestockPage = lazy(() => import("@/pages/admin/SmartRestockPage"));
const AiLegalAdvisorPage = lazy(() => import("@/pages/admin/AiLegalAdvisorPage"));
const WhatsAppSettingsPage = lazy(() => import("@/pages/admin/WhatsAppSettingsPage"));
const AICreditsPage = lazy(() => import("@/pages/admin/AICreditsPage"));
const SaasRenewCryptoPage = lazy(() => import("@/pages/admin/SaasRenewCryptoPage"));
const LifetimeBuyoutPage = lazy(() => import("@/pages/admin/LifetimeBuyoutPage"));
const WhatsAppOutboxPage = lazy(() => import("@/pages/admin/WhatsAppOutboxPage"));
const WhatsAppWebPage = lazy(() => import("@/pages/admin/WhatsAppWebPage"));
const SyncQueuePage = lazy(() => import("@/pages/admin/SyncQueuePage"));
const DevStudioPage = lazy(() => import("@/pages/admin/DevStudioPage"));
const AdminKdsPage = lazy(() => import("@/pages/admin/KdsPage"));
const StockLossPage = lazy(() => import("@/pages/admin/StockLossPage"));
const ShrinkageReportPage = lazy(() => import("@/pages/admin/ShrinkageReportPage"));
const ExpensesPage = lazy(() => import("@/pages/admin/ExpensesPage"));
const TenantsPage = lazy(() => import("@/pages/admin/TenantsPage"));
const RemoteDevicesPage = lazy(() => import("@/pages/admin/RemoteDevicesPage"));
const TrialSignupsPage = lazy(() => import("@/pages/admin/TrialSignupsPage"));
const SuperAdminHelpPage = lazy(() => import("@/pages/admin/SuperAdminHelpPage"));
const OpsCenterPage = lazy(() => import("@/pages/admin/OpsCenterPage"));
const SaasPlansPage = lazy(() => import("@/pages/admin/SaasPlansPage"));
const PublicManualPage = lazy(() => import("@/pages/PublicManualPage"));
const InstallPage = lazy(() => import("@/pages/InstallPage"));
const MonitorPage = lazy(() => import("@/pages/admin/MonitorPage"));
const ReconciliationPage = lazy(() => import("@/pages/admin/ReconciliationPage"));
const ShopQRPosterPage = lazy(() => import("@/pages/admin/ShopQRPosterPage"));
const PromoStatusPage = lazy(() => import("@/pages/admin/PromoStatusPage"));
const GoogleMapsWizardPage = lazy(() => import("@/pages/admin/GoogleMapsWizardPage"));
const ReferralLeaderboardPage = lazy(() => import("@/pages/admin/ReferralLeaderboardPage"));
const ReferralProgramPage = lazy(() => import("@/pages/admin/ReferralPage"));
const ShopAnalyticsPage = lazy(() => import("@/pages/admin/ShopAnalyticsPage"));
const VisionPage = lazy(() => import("@/pages/admin/VisionPage"));
const DeliveryRidersPage = lazy(() => import("@/pages/admin/DeliveryRidersPage"));
const RatingsPage = lazy(() => import("@/pages/admin/RatingsPage"));
const IndustrySetupPage = lazy(() => import("@/pages/admin/IndustrySetupPage"));
const RestaurantTablesPage = lazy(() => import("@/pages/admin/RestaurantTablesPage"));
const GiftCardsPage = lazy(() => import("@/pages/admin/GiftCardsPage"));
const AppointmentsPage = lazy(() => import("@/pages/admin/AppointmentsPage"));
const ServicePackagesPage = lazy(() => import("@/pages/admin/ServicePackagesPage"));
const BeautyPage = lazy(() => import("@/pages/admin/BeautyPage"));
const CustomerDisplayPage = lazy(() => import("@/pages/CustomerDisplayPage"));
const LotsPage = lazy(() => import("@/pages/admin/LotsPage"));
const RFMPage = lazy(() => import("@/pages/admin/RFMPage"));
const StaffPerformancePage = lazy(() => import("@/pages/admin/StaffPerformancePage"));
const OrphanCreditsPage = lazy(() => import("@/pages/admin/OrphanCreditsPage"));
const PriceCheckPage = lazy(() => import("@/pages/PriceCheckPage"));

// PwaInstallPrompt removed per user request (Aug 27 2026)
import BrowserWarning from "@/components/BrowserWarning";
import { UpdateChecker } from "@/components/UpdateChecker";

// Loading spinner for lazy-loaded pages
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-screen bg-slate-900">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-slate-400 text-sm">Loading...</span>
      </div>
    </div>
  );
}

function PosGuard({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><div className="text-white">Loading...</div></div>;
  // Allow access if AuthContext has user OR if POS has cached session (offline scenario)
  const hasPosSession = !user && localStorage.getItem("pos_user");
  if (!user && !hasPosSession) return <Navigate to="/login" replace />;
  if (user?.role === 'price_checker') return <Navigate to="/price-check" replace />;
  if (user?.role === 'stock_viewer') return <Navigate to="/price-check" replace />;
  return children;
}

const WS_CLERK_ALLOWED = new Set(["/admin/wholesale", "/admin/online-orders", "/admin/customers"]);
const SHOP_ADMIN_PATHS = ["/admin/online-orders", "/admin/group-buy", "/admin/delivery", "/admin/ratings", "/admin/shop-qr-poster", "/admin/google-maps", "/admin/referrals", "/admin/referral-program", "/admin/shop-analytics", "/admin/shop-marketing"];

function AdminRoute({ children, requiredPermission, superOnly }) {
  const { user, permissions, canAccessAdmin, hasPermission, loading } = useAuth();
  const location = useLocation();

  // Wait for /auth/me to finish resolving. Without this, a refresh on a
  // super-admin page briefly uses the stale cached_auth_user (which may still
  // carry a tenant_id from an earlier tenant login in this browser) and
  // bounces the user to /admin. Loading screen prevents that race.
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-400">Loading...</div>;
  }

  // store managers live in the POS; the only admin page they may open is RRHH Legal (amonestaciones/actas of their store)
  const smHrOnly = (permissions?.role || user?.role) === "store_manager" && ["/admin/rrhh-legal", "/admin/attendance"].includes(location.pathname);
  if (user && !canAccessAdmin() && !smHrOnly) {
    return <Navigate to="/pos" replace />;
  }

  // 🏭 批发开单员 — locked to wholesale ordering + online orders only
  if (user && (permissions?.role || user.role) === "wholesale_clerk" && !WS_CLERK_ALLOWED.has(location.pathname)) {
    return <Navigate to="/admin/wholesale" replace />;
  }

  if (superOnly && user && (user.role !== 'admin' || user.tenant_id)) {
    return <Navigate to="/admin" replace />;
  }

  if (requiredPermission && user && !hasPermission(requiredPermission)) {
    return <Navigate to="/admin" replace />;
  }

  // 🛒 plan gate: 普通版 (lifetime_1) has no online shop → its admin pages bounce to the upgrade page
  if (user?.tenant?.has_online_shop === false && SHOP_ADMIN_PATHS.some((p) => location.pathname.startsWith(p))) {
    return <Navigate to="/admin/lifetime-buyout" replace />;
  }

  return (
    <ProtectedRoute>
      <AdminLayout>{children}</AdminLayout>
    </ProtectedRoute>
  );
}

function AppContent() {
  const [, setCurTick] = useState(0);
  useEffect(() => {
    // 💱 shop currency symbol arrived/changed → re-render everything once so "$"→"¥" switches instantly
    const h = () => setCurTick(x => x + 1);
    window.addEventListener("sx-cur-change", h);
    return () => window.removeEventListener("sx-cur-change", h);
  }, []);
  useEffect(() => {
    // 🛡️ Radix leftover guard: Select/Dialog can leave body stuck with pointer-events:none
    const obs = new MutationObserver(() => {
      if (document.body.style.pointerEvents === 'none' &&
          !document.querySelector('[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"], [data-radix-popper-content-wrapper]')) {
        document.body.style.pointerEvents = '';
      }
    });
    obs.observe(document.body, { attributes: true, attributeFilter: ['style'] });
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    // 🎨 Store-wide UI theme: instant from cache, then refresh from server
    applyCachedTheme();
    setTimeout(() => {
      if (localStorage.getItem("token") || localStorage.getItem("pos_token")) {
        axios.get(`${API}/settings/system`, { skipGlobalError: true, timeout: 8000 })
          .then(r => {
            applyStoreTheme(r.data?.ui_theme, r.data?.ui_accent); setCurSym(r.data?.currency_symbol);
            const s = r.data || {};
            rememberRawDriver(s.normal_print_method === 'raw_driver' || s.fiscal_print_method === 'raw_driver', s.pos_printer_name || '', s.receipt_paper);
          }).catch(() => {});
      }
    }, 1200);
    // 🔊 Toast bridge: replay + subscribe to toasts fired from non-React
    // modules (sync queue, axios interceptors) — see utils/toastBus.js
    const showBusToast = (d) => {
      if (!d) return;
      if (d.kind === "dismiss") { toast.dismiss(d.title); return; }
      (toast[d.kind] || toast)(d.title, d.opts || {});
    };
    drainToastBuffer(showBusToast);
    const onBusToast = (e) => showBusToast(e.detail);
    window.addEventListener("sellox-toast", onBusToast);
    // Init-data is a fire-and-forget bootstrap call — it should never surface a
    // "保存失败" toast to the user, even when the network is flaky. The
    // skipGlobalError flag bypasses the global axios interceptor.
    axios.post(`${API}/init-data`, null, { skipGlobalError: true, timeout: 8000 }).catch(() => {});
    // Install offline sync interceptor
    installOfflineInterceptor((msg) => toast.info(msg));
    // Offline read cache: pages keep showing data without network
    installOfflineReadCache();
    // Auto-sync when coming back online
    const handleOnline = async () => {
      const count = await getQueueCount();
      if (count > 0) {
        toast.info(`网络已恢复，正在同步 ${count} 条离线数据...`);
        const result = await replayQueue((p) => {
          if (p.synced % 5 === 0) toast.info(`同步中... ${p.synced}/${p.total}`);
        });
        if (result.synced > 0) toast.success(`已同步 ${result.synced} 条离线数据`);
        if (result.failed > 0) toast.warning(`${result.failed} 条同步失败，将在下次联网重试`);
      }
    };
    window.addEventListener('online', handleOnline);
    // Also try sync on load if online
    if (navigator.onLine) handleOnline();
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('sellox-toast', onBusToast);
    };
  }, []);

  return (
    <AuthProvider>
      <IndustryProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <UpdateChecker />
        <BrowserWarning />
        <ApkUpdateChecker />
        <DesktopAutoUpdate />
        <RemoteAutoJoin />
        <CacheWarmer />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {SHOP_SUB && (
              <>
                <Route path="/" element={<LangProvider storageKey="shop_lang" defaultLang="es"><ShopPage slugOverride={SHOP_SUB} /></LangProvider>} />
                <Route path="/mayor" element={<LangProvider storageKey="shop_lang" defaultLang="es"><ShopPage slugOverride={SHOP_SUB} channel="wholesale" /></LangProvider>} />
                <Route path="/orders" element={<ShopOrdersPage />} />
                <Route path="/como-comprar" element={<ShopHowToBuyPage slugOverride={SHOP_SUB} />} />
              </>
            )}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/terminos" element={<TerminosPage />} />
            <Route path="/manual" element={<PublicManualPage />} />
            <Route path="/instalar" element={<InstallPage />} />
            <Route path="/install" element={<InstallPage />} />
            <Route path="/shop/:tenantId" element={<LangProvider storageKey="shop_lang" defaultLang="es"><ShopPage /></LangProvider>} />
            <Route path="/portal/:tenantId" element={<FranchisePortalPage />} />
            <Route path="/shop/:tenantId/mayor" element={<LangProvider storageKey="shop_lang" defaultLang="es"><ShopPage channel="wholesale" /></LangProvider>} />
            <Route path="/shop/:tenantId/orders" element={<ShopOrdersPage />} />
            <Route path="/shop/:tenantId/como-comprar" element={<ShopHowToBuyPage />} />
            <Route path="/catalog/:tenantId" element={<CatalogPage />} />
            <Route path="/rider" element={<RiderApp />} />
            <Route path="/track/:tenantId/:orderId" element={<RiderTrackPage />} />
            <Route path="/truck/:tenantId/:token" element={<TruckDriverPage />} />
            <Route path="/rate/:tenantId/:orderId" element={<RateOrderPage />} />
            <Route path="/receipt/:tenantId/:orderId" element={<ReceiptPublicPage />} />
            <Route path="/aparta/:tenantId/:aptId" element={<ApartaPublicPage />} />
            <Route path="/upload-product-image/:token" element={<ProductImageUploadPublicPage />} />
            <Route path="/upload-product-images/:token" element={<BatchImageUploadPublicPage />} />
            <Route path="/boss/camera" element={<ProtectedRoute><BossCameraPage /></ProtectedRoute>} />
            <Route path="/employee-statement/:tenantId/:employeeId" element={<EmployeeStatementPublicPage />} />
            <Route path="/customer-statement/:tenantId/:customerId" element={<CustomerStatementPublicPage />} />
            <Route path="/admin-manual-6688" element={<AdminSecretManual />} />
            <Route path="/pos" element={<PosGuard><PosPage /></PosGuard>} />
            <Route path="/print-setup" element={<PrintSetupPage />} />
            <Route path="/kds" element={<ProtectedRoute><KdsPage /></ProtectedRoute>} />
            <Route path="/llamador" element={<ProtectedRoute><PickupScreenPage /></ProtectedRoute>} />
            <Route path="/kiosk/attendance" element={<ProtectedRoute><AttendanceKioskPage /></ProtectedRoute>} />
            <Route path="/clock" element={<ProtectedRoute><PhoneClockPage /></ProtectedRoute>} />
            <Route path="/customer-display" element={<CustomerDisplayPage />} />
            <Route path="/pos/mobile" element={<MobilePosPage />} />
            <Route path="/member/lookup" element={<MemberLookupPage />} />
            <Route path="/mis-puntos" element={<PublicPointsPage />} />
            <Route path="/studio" element={<CodeStudioPage />} />
            <Route path="/my-points" element={<PublicPointsPage />} />
            <Route path="/verify/:tenantId/:storeId" element={<VerifyPaymentPage />} />
            <Route path="/verify/:storeId" element={<VerifyPaymentPage />} />

            <Route path="/admin" element={<AdminRoute><Dashboard /></AdminRoute>} />
            <Route path="/admin/stores" element={<AdminRoute><StoresPage /></AdminRoute>} />
            <Route path="/admin/store-prices" element={<AdminRoute><StorePricesPage /></AdminRoute>} />
            <Route path="/admin/store-overview" element={<AdminRoute><ReportsHubPage tab="hq" /></AdminRoute>} />
            <Route path="/admin/warehouses" element={<AdminRoute><WarehousesPage /></AdminRoute>} />
            <Route path="/admin/products" element={<AdminRoute><ProductsPage /></AdminRoute>} />
            <Route path="/admin/transfers" element={<AdminRoute><TransferPage /></AdminRoute>} />
            <Route path="/admin/customers" element={<AdminRoute><CustomersPage /></AdminRoute>} />
            <Route path="/admin/suppliers" element={<AdminRoute><SuppliersPage /></AdminRoute>} />
            <Route path="/admin/payables" element={<AdminRoute><PayablesPage /></AdminRoute>} />
            <Route path="/admin/vouchers" element={<AdminRoute><VouchersPage /></AdminRoute>} />
            <Route path="/admin/price-changes" element={<AdminRoute><PriceChangesPage /></AdminRoute>} />
            <Route path="/admin/customs" element={<AdminRoute><CustomsPage /></AdminRoute>} />
            <Route path="/admin/fabrica" element={<AdminRoute><FabricaPage /></AdminRoute>} />
            <Route path="/admin/franchise" element={<AdminRoute><FranchisePage /></AdminRoute>} />
            <Route path="/admin/purchases" element={<AdminRoute><PurchasesPage /></AdminRoute>} />
            <Route path="/admin/whatsapp" element={<AdminRoute><WhatsAppSettingsPage /></AdminRoute>} />
            <Route path="/admin/ai-credits" element={<AdminRoute><AICreditsPage /></AdminRoute>} />
            <Route path="/admin/saas-renew" element={<AdminRoute><SaasRenewCryptoPage /></AdminRoute>} />
            <Route path="/admin/lifetime-buyout" element={<AdminRoute><LifetimeBuyoutPage /></AdminRoute>} />
            <Route path="/admin/whatsapp-outbox" element={<AdminRoute><WhatsAppOutboxPage /></AdminRoute>} />
            <Route path="/admin/whatsapp-web" element={<AdminRoute><WhatsAppWebPage /></AdminRoute>} />
            <Route path="/admin/sync-queue" element={<AdminRoute><SyncQueuePage /></AdminRoute>} />
            <Route path="/admin/dev-studio" element={<AdminRoute><DevStudioPage /></AdminRoute>} />
            <Route path="/admin/kds" element={<AdminRoute><AdminKdsPage /></AdminRoute>} />
            <Route path="/admin/sales" element={<AdminRoute><SalesPage /></AdminRoute>} />
            <Route path="/admin/online-orders" element={<AdminRoute><OnlineOrdersPage /></AdminRoute>} />
            <Route path="/admin/sales-report" element={<AdminRoute><SalesReportPage /></AdminRoute>} />
            <Route path="/admin/category-analysis" element={<AdminRoute><ReportsHubPage tab="category" /></AdminRoute>} />
            <Route path="/admin/reports" element={<AdminRoute><ReportsHubPage tab="reports" /></AdminRoute>} />
            <Route path="/admin/tax-report" element={<AdminRoute><ReportsHubPage tab="tax" /></AdminRoute>} />
            <Route path="/admin/contabilidad" element={<AdminRoute><ContabilidadPage /></AdminRoute>} />
            <Route path="/admin/caja-ebr" element={<AdminRoute><CajaEbrPage /></AdminRoute>} />
            <Route path="/admin/wholesale-report" element={<AdminRoute><ReportsHubPage tab="wholesale" /></AdminRoute>} />
            <Route path="/admin/exchange-rates" element={<AdminRoute><ExchangeRatesPage /></AdminRoute>} />
            <Route path="/admin/bank-accounts" element={<AdminRoute><BankAccountsPage /></AdminRoute>} />
            <Route path="/admin/cashea" element={<AdminRoute><CasheaReconcilePage /></AdminRoute>} />
            <Route path="/admin/payment-settings" element={<AdminRoute><PaymentSettingsPage /></AdminRoute>} />
            <Route path="/admin/settings" element={<AdminRoute><SystemSettingsPage /></AdminRoute>} />
            <Route path="/admin/doc-design" element={<AdminRoute><DocDesignPage /></AdminRoute>} />
            <Route path="/admin/tutorials" element={<AdminRoute><TutorialsPage /></AdminRoute>} />
            <Route path="/admin/printer-guide" element={<AdminRoute><PrinterGuidePage /></AdminRoute>} />
            <Route path="/admin/employees" element={<AdminRoute><EmployeesPage /></AdminRoute>} />
            <Route path="/admin/stock-alerts" element={<AdminRoute><StockAlertsPage /></AdminRoute>} />
            <Route path="/admin/stock-taking" element={<AdminRoute><StockTakingPage /></AdminRoute>} />
            <Route path="/admin/categories" element={<AdminRoute><CategoriesPage /></AdminRoute>} />
            <Route path="/admin/daily-settlement" element={<AdminRoute><DailySettlementPage /></AdminRoute>} />
            <Route path="/admin/points-monthly" element={<AdminRoute><PointsMonthlyPage /></AdminRoute>} />
            <Route path="/admin/refunds" element={<AdminRoute><RefundsPage /></AdminRoute>} />
            <Route path="/admin/profit-analysis" element={<AdminRoute><ProfitAnalysisPage /></AdminRoute>} />
            <Route path="/admin/top-sellers" element={<AdminRoute><TopSellersPage /></AdminRoute>} />
            <Route path="/admin/slow-movers" element={<AdminRoute><SlowMoversPage /></AdminRoute>} />
            <Route path="/admin/audit-log" element={<AdminRoute><AuditLogPage /></AdminRoute>} />
            <Route path="/admin/trusted-devices" element={<AdminRoute><TrustedDevicesPage /></AdminRoute>} />
            <Route path="/admin/device-approvals" element={<AdminRoute superOnly><DeviceApprovalsPage /></AdminRoute>} />
            <Route path="/admin/boss-pin" element={<AdminRoute><BossPinPage /></AdminRoute>} />
            <Route path="/admin/promotions" element={<AdminRoute><PromotionsPage /></AdminRoute>} />
            <Route path="/admin/shop-marketing" element={<AdminRoute><ShopMarketingPage /></AdminRoute>} />
            <Route path="/admin/sorteo" element={<AdminRoute><SorteoPage /></AdminRoute>} />
            <Route path="/admin/rrhh-legal" element={<AdminRoute><HrLegalPage /></AdminRoute>} />
            <Route path="/admin/accounts" element={<AdminRoute><AccountsPage /></AdminRoute>} />
            <Route path="/admin/wholesale" element={<AdminRoute><WholesalePage /></AdminRoute>} />
            <Route path="/admin/attendance" element={<AdminRoute><AttendancePage /></AdminRoute>} />
            <Route path="/admin/sales-targets" element={<AdminRoute><SalesTargetsPage /></AdminRoute>} />
            <Route path="/admin/purchase-returns" element={<AdminRoute><PurchaseReturnsPage /></AdminRoute>} />
            <Route path="/admin/bundles" element={<AdminRoute><BundlesPage /></AdminRoute>} />
            <Route path="/admin/notifications" element={<AdminRoute><NotificationsPage /></AdminRoute>} />
            <Route path="/admin/commission" element={<AdminRoute><CommissionPage /></AdminRoute>} />
            <Route path="/admin/tenants" element={<AdminRoute superOnly><TenantsPage /></AdminRoute>} />
            <Route path="/admin/remote-devices" element={<AdminRoute superOnly><RemoteDevicesPage /></AdminRoute>} />
            <Route path="/admin/trial-signups" element={<AdminRoute superOnly><TrialSignupsPage /></AdminRoute>} />
            <Route path="/admin/super-admin-help" element={<AdminRoute><SuperAdminHelpPage /></AdminRoute>} />
            <Route path="/admin/help" element={<Navigate to="/manual" replace />} />
            <Route path="/admin/video-tutorials" element={<AdminRoute><VideoTutorialsPage /></AdminRoute>} />
            <Route path="/admin/analytics" element={<AdminRoute><ReportsHubPage /></AdminRoute>} />
            <Route path="/admin/shift-reports" element={<AdminRoute><ShiftReportsPage /></AdminRoute>} />
            <Route path="/admin/shift-reconcile" element={<AdminRoute><ShiftReconcilePage /></AdminRoute>} />
            <Route path="/admin/email-reports" element={<AdminRoute><EmailReportsPage /></AdminRoute>} />
            <Route path="/admin/loyalty" element={<AdminRoute><LoyaltyProgramPage /></AdminRoute>} />
            <Route path="/admin/points-mall" element={<AdminRoute><PointsMallPage /></AdminRoute>} />
            <Route path="/admin/sales-channels" element={<AdminRoute><ReportsHubPage tab="channels" /></AdminRoute>} />
            <Route path="/admin/credits" element={<AdminRoute><CreditPage /></AdminRoute>} />
            <Route path="/admin/fiscal" element={<AdminRoute><FiscalPage /></AdminRoute>} />
            <Route path="/admin/einvoice" element={<AdminRoute><EInvoicePage /></AdminRoute>} />
            <Route path="/admin/marketing" element={<AdminRoute><MarketingPage /></AdminRoute>} />
            <Route path="/admin/marketing-auto" element={<AdminRoute><MarketingAutoPage /></AdminRoute>} />
            <Route path="/admin/marketing-blast" element={<AdminRoute><MarketingBlastPage /></AdminRoute>} />
            <Route path="/admin/ai-credits" element={<AdminRoute><AiCreditsTopupPage /></AdminRoute>} />
            <Route path="/admin/ai-credits/success" element={<AdminRoute><AiCreditsTopupPage /></AdminRoute>} />
            <Route path="/admin/ai-credits/cancel" element={<AdminRoute><AiCreditsTopupPage /></AdminRoute>} />
            <Route path="/admin/apartados" element={<AdminRoute><ApartadosPage /></AdminRoute>} />
            <Route path="/admin/partner-ledger" element={<AdminRoute><PartnerLedgerPage /></AdminRoute>} />
            <Route path="/admin/fx-alerts" element={<AdminRoute><FxAlertPage /></AdminRoute>} />
            <Route path="/admin/intl-payment" element={<AdminRoute superOnly><IntlPaymentPage /></AdminRoute>} />
            <Route path="/admin/points-qr" element={<AdminRoute><PointsQRPosterPage /></AdminRoute>} />
            <Route path="/admin/super-country" element={<AdminRoute superOnly><SuperCountryPage /></AdminRoute>} />
            <Route path="/admin/super-traffic" element={<AdminRoute superOnly><SuperTrafficPage /></AdminRoute>} />
            <Route path="/admin/super-crypto-income" element={<AdminRoute superOnly><SuperCryptoIncomePage /></AdminRoute>} />
            <Route path="/admin/super-referral-commissions" element={<AdminRoute superOnly><SuperReferralCommissionsPage /></AdminRoute>} />
            <Route path="/admin/super-direct-pay" element={<AdminRoute superOnly><SuperDirectPayPage /></AdminRoute>} />
            <Route path="/admin/super-manual-topups" element={<AdminRoute superOnly><SuperManualTopupsPage /></AdminRoute>} />
            <Route path="/admin/super-ai-usage" element={<AdminRoute superOnly><SuperAIUsagePage /></AdminRoute>} />
            <Route path="/admin/group-buy" element={<AdminRoute><GroupBuyPage /></AdminRoute>} />
            <Route path="/vendedor" element={<VendedorApp />} />
            <Route path="/contar" element={<CountTaskPage />} />
            <Route path="/tv/:tenantId" element={<TvPage />} />
            <Route path="/tv/:tenantId/sorteo" element={<SorteoTvPage />} />
            <Route path="/tv/:tenantId/cameras" element={<TvCamerasPage />} />
            <Route path="/sorteo/:tenantId" element={<SorteoRegisterPage />} />
            <Route path="/admin/picking" element={<AdminRoute><PickingPage /></AdminRoute>} />
            <Route path="/admin/super-subscriptions" element={<AdminRoute superOnly><SuperSubscriptionsPage /></AdminRoute>} />
            <Route path="/admin/super-tickets" element={<AdminRoute superOnly><SuperTicketsPage /></AdminRoute>} />
            <Route path="/admin/super-release-notes" element={<AdminRoute superOnly><SuperReleaseNotesPage /></AdminRoute>} />
            <Route path="/admin/support" element={<AdminRoute><SupportTicketsPage /></AdminRoute>} />
            <Route path="/admin/b2b-portal" element={<AdminRoute><B2BPortalPage /></AdminRoute>} />
            <Route path="/admin/smart-restock" element={<AdminRoute><SmartRestockPage /></AdminRoute>} />
            <Route path="/admin/ai-legal" element={<AdminRoute><AiLegalAdvisorPage /></AdminRoute>} />
            <Route path="/admin/stock-loss" element={<AdminRoute><StockLossPage /></AdminRoute>} />
            <Route path="/admin/shrinkage" element={<AdminRoute><ShrinkageReportPage /></AdminRoute>} />
            <Route path="/admin/expenses" element={<AdminRoute><ExpensesPage /></AdminRoute>} />
            <Route path="/admin/monitor" element={<AdminRoute superOnly><MonitorPage /></AdminRoute>} />
            <Route path="/admin/ops" element={<AdminRoute superOnly><OpsCenterPage /></AdminRoute>} />
            <Route path="/admin/saas-plans" element={<AdminRoute superOnly><SaasPlansPage /></AdminRoute>} />
            <Route path="/admin/reconciliation" element={<AdminRoute><ReconciliationPage /></AdminRoute>} />
            <Route path="/admin/shop-qr-poster" element={<AdminRoute><ShopQRPosterPage /></AdminRoute>} />
            <Route path="/admin/promo-status" element={<AdminRoute><PromoStatusPage /></AdminRoute>} />
            <Route path="/admin/google-maps" element={<AdminRoute><GoogleMapsWizardPage /></AdminRoute>} />
            <Route path="/admin/referrals" element={<AdminRoute><ReferralLeaderboardPage /></AdminRoute>} />
            <Route path="/admin/referral-program" element={<AdminRoute><ReferralProgramPage /></AdminRoute>} />
            <Route path="/admin/shop-analytics" element={<AdminRoute><ShopAnalyticsPage /></AdminRoute>} />
            <Route path="/admin/vision" element={<AdminRoute><VisionPage /></AdminRoute>} />
            <Route path="/admin/delivery" element={<AdminRoute><DeliveryRidersPage /></AdminRoute>} />
            <Route path="/admin/ratings" element={<AdminRoute><RatingsPage /></AdminRoute>} />
            <Route path="/admin/industry" element={<AdminRoute><IndustrySetupPage /></AdminRoute>} />
            <Route path="/admin/restaurant-tables" element={<AdminRoute><RestaurantTablesPage /></AdminRoute>} />
            <Route path="/admin/gift-cards" element={<AdminRoute><GiftCardsPage /></AdminRoute>} />
            <Route path="/admin/appointments" element={<AdminRoute><AppointmentsPage /></AdminRoute>} />
            <Route path="/admin/service-packages" element={<AdminRoute><ServicePackagesPage /></AdminRoute>} />
            <Route path="/admin/beauty" element={<AdminRoute><BeautyPage /></AdminRoute>} />
            <Route path="/admin/lots" element={<AdminRoute><LotsPage /></AdminRoute>} />
            <Route path="/admin/rfm" element={<AdminRoute><RFMPage /></AdminRoute>} />
            <Route path="/admin/staff-performance" element={<AdminRoute><StaffPerformancePage /></AdminRoute>} />
            <Route path="/admin/orphan-credits" element={<AdminRoute><OrphanCreditsPage /></AdminRoute>} />
            <Route path="/price-check" element={<ProtectedRoute><PriceCheckPage /></ProtectedRoute>} />

            {!SHOP_SUB && <Route path="/" element={<LandingPage />} />}
            <Route path="/international" element={<LandingInternational />} />
            <Route path="/za" element={<LandingZA />} />
            <Route path="/co" element={<LandingCO />} />
            <Route path="/br" element={<LandingBR />} />
            <Route path="/cr" element={<LandingCR />} />
            <Route path="/tt" element={<LandingTT />} />
            <Route path="/do" element={<LandingDO />} />
            <Route path="/pa" element={<LandingPA />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/admin/sales-docs" element={<AdminRoute><SalesDocsPage /></AdminRoute>} />
            <Route path="/ve" element={<LandingPage />} />
            <Route path="/saas/checkout" element={<SaasCheckoutPage />} />
            <Route path="/saas/success" element={<SaasSuccessPage />} />
            <Route path="/saas/cancel" element={<div className="min-h-screen bg-slate-950 text-white flex items-center justify-center"><div className="text-center"><h2 className="text-2xl font-bold mb-2">Payment cancelled</h2><p className="text-slate-400 mb-5">No worries — you can try again anytime.</p><a href="/" className="text-emerald-400 hover:underline">← Back home</a></div></div>} />
            <Route path="/trial" element={<LandingPage />} />
            <Route path="*" element={<div className="min-h-screen bg-slate-950 text-white flex items-center justify-center"><div className="text-center" data-testid="not-found-page"><div className="text-6xl font-extrabold text-emerald-500 mb-2">404</div><p className="text-slate-400 mb-5">{tr('x_fix_404')}</p><a href="/" className="text-emerald-400 hover:underline">{tr('x_fix_404_home')}</a></div></div>} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      </IndustryProvider>
    </AuthProvider>
  );
}

function App() {
  return (
    <LangProvider>
      <CountryProvider>
        {/* PWA install prompt removed per user request (Aug 27 2026) */}
        <AppContent />
      </CountryProvider>
    </LangProvider>
  );
}

export default App;
